import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Handles the redirect back from Emergent-managed Google Auth.
 * Accepts the session_id in either the URL fragment (`#session_id=…`) OR the
 * query string (`?session_id=…`) — the auth service has been observed to use
 * both. Surfaces the specific server error if the exchange fails so users
 * (and support) can act on it instead of the generic "Sign-in failed" toast.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const hash = window.location.hash || "";
    const search = window.location.search || "";
    const combined = hash + "&" + search.replace(/^\?/, "");
    const match = combined.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      const msg = "Missing session ID in the redirect URL — please try signing in again.";
      setError(msg);
      toast.error(msg);
      const t = setTimeout(() => navigate("/portal"), 3500);
      return () => clearTimeout(t);
    }

    api.post("/auth/google/session", { session_id: sessionId })
      .then(({ data }) => {
        if (data?.user) setUser(data.user);
        toast.success(`Welcome${data?.user?.name ? `, ${data.user.name.split(" ")[0]}` : ""}!`);
        // Return to the portal so admins land inside their dashboard.
        navigate(data?.user?.role === "admin" ? "/admin" : "/portal");
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail || e?.message || "Unknown error";
        const msg = `Google sign-in failed: ${detail}`;
        setError(msg);
        toast.error(msg);
        const t = setTimeout(() => navigate("/portal"), 5000);
        return () => clearTimeout(t);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#FDF9F1] flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <div className="w-12 h-12 mx-auto mb-4 rounded-full border-4 border-[#EADCC8] border-t-[#C86650] animate-spin" />
        {error ? (
          <>
            <p className="font-serif text-xl text-[#C86650] mb-2">Sign-in error</p>
            <p className="text-sm text-[#5C6A64] mb-2 break-words" data-testid="auth-callback-error">{error}</p>
            <p className="text-xs text-[#5C6A64]">Returning you to sign-in in a moment…</p>
          </>
        ) : (
          <p className="font-serif text-xl text-[#1A2421]">Completing sign-in…</p>
        )}
      </div>
    </div>
  );
}
